const SLIDER_MIN = 1;
const SLIDER_MAX = 7;
const DEFAULT_SLIDER_VALUE = 4;

const SLIDER_GRADIENT_START = "#00aeef";
const SLIDER_GRADIENT_END = "#ec008c";

// Discrete identity palette: the slider gradient's endpoints plus its exact
// midpoint. A member's chat color snaps to one of these three values — never
// an in-between gradient mix — matching the backend's
// `computeChatColorFromProfile` in amplify/functions/userProfileHandler/core.js.
const CHAT_COLOR_BLUE = SLIDER_GRADIENT_START;
const CHAT_COLOR_PURPLE = "#7657be";
const CHAT_COLOR_MAGENTA = SLIDER_GRADIENT_END;
const CHAT_COLOR_PALETTE = Object.freeze([
  CHAT_COLOR_BLUE,
  CHAT_COLOR_PURPLE,
  CHAT_COLOR_MAGENTA,
]);
const CHAT_COLOR_PALETTE_SET = new Set(CHAT_COLOR_PALETTE);
// Average-answer boundaries between the three buckets.
const CHAT_COLOR_BLUE_MAX_AVG = 3;
const CHAT_COLOR_MAGENTA_MIN_AVG = 5;

/**
 * A stored chatColor only counts when it is one of the three palette colors.
 * Legacy gradient-interpolated values (any other hex) read as missing so the
 * color is recomputed from the member's answers instead.
 */
function normalizeStoredChatColor(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  if (!/^#[0-9a-fA-F]{6}$/.test(withHash)) return "";
  const normalized = withHash.toLowerCase();
  return CHAT_COLOR_PALETTE_SET.has(normalized) ? normalized : "";
}

function snapAverageToChatColor(averageValue) {
  if (averageValue < CHAT_COLOR_BLUE_MAX_AVG) return CHAT_COLOR_BLUE;
  if (averageValue > CHAT_COLOR_MAGENTA_MIN_AVG) return CHAT_COLOR_MAGENTA;
  return CHAT_COLOR_PURPLE;
}

function getSociopoliticalAnswers(profileLike) {
  if (!profileLike || typeof profileLike !== "object") return {};

  if (profileLike.sociopoliticalAnswers && typeof profileLike.sociopoliticalAnswers === "object") {
    return profileLike.sociopoliticalAnswers;
  }
  if (profileLike.sociopolitical && typeof profileLike.sociopolitical === "object") {
    return profileLike.sociopolitical;
  }
  if (profileLike.profile && typeof profileLike.profile === "object") {
    return getSociopoliticalAnswers(profileLike.profile);
  }
  return {};
}

function getStoredChatColor(profileLike) {
  if (!profileLike || typeof profileLike !== "object") return "";

  const direct = normalizeStoredChatColor(profileLike.chatColor);
  if (direct) return direct;

  if (profileLike.profile && typeof profileLike.profile === "object") {
    return getStoredChatColor(profileLike.profile);
  }

  return "";
}

function getAverageSliderValue(profileLike) {
  const answers = getSociopoliticalAnswers(profileLike);
  const values = Object.values(answers)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= SLIDER_MIN && value <= SLIDER_MAX);

  if (!values.length) return DEFAULT_SLIDER_VALUE;

  const total = values.reduce((sum, value) => sum + value, 0);
  return total / values.length;
}

export function pickChatColor(profileLike) {
  const storedChatColor = getStoredChatColor(profileLike);
  if (storedChatColor) return storedChatColor;

  return snapAverageToChatColor(getAverageSliderValue(profileLike));
}

export { SLIDER_GRADIENT_START, SLIDER_GRADIENT_END, CHAT_COLOR_PALETTE };
